import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import GridBackground from './GridBackground';

function LandingPage() {
  useEffect(() => {
    // Load the widget script dynamically only for the landing page
    const loadWidget = () => {
      // Check if widget is already loaded (script or widget elements exist)
      if (document.querySelector('script[src*="widget.js"]') || 
          document.getElementById('ai-chat-widget') ||
          document.getElementById('ai-chat-toggle')) {
        console.log('Widget already exists, skipping load');
        return;
      }

      const script = document.createElement('script');
      
      // Auto-detect environment for widget URL
      const hostname = window.location.hostname;
      let widgetUrl;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.0.')) {
        widgetUrl = 'http://localhost:3002/widget.js';
      } else {
        widgetUrl = 'https://smartweb-ai-backend-343646917543.europe-west1.run.app/widget.js';
      }
      
      script.src = widgetUrl;
      
      // Debug environment variables
      console.log('Environment variables:', {
        apiKey: process.env.REACT_APP_DEMO_API_KEY,
        projectId: process.env.REACT_APP_DEMO_PROJECT_ID
      });
      
      const apiKey = process.env.REACT_APP_DEMO_API_KEY || 'your-api-key-here';
      const projectId = process.env.REACT_APP_DEMO_PROJECT_ID || 'your-project-id-here';
      
      script.setAttribute('data-api-key', apiKey);
      script.setAttribute('data-project-id', projectId);
      
      console.log('Widget script attributes:', { apiKey, projectId });
      
      script.onload = () => {
        console.log('Smart Web Chat widget loaded successfully on landing page');
      };
      
      script.onerror = () => {
        console.error('Failed to load Smart Web Chat widget');
      };
      
      document.head.appendChild(script);
    };

    // Add a small delay to ensure DOM is ready and avoid race conditions
    const timer = setTimeout(() => {
      loadWidget();
    }, 100);

    // Cleanup function to remove widget when leaving landing page
    return () => {
      clearTimeout(timer);
      
      // Remove widget script and elements when component unmounts
      const widgetScript = document.querySelector('script[src*="widget.js"]');
      if (widgetScript) {
        widgetScript.remove();
      }
      
      // Remove widget elements
      const widgetContainer = document.getElementById('ai-chat-widget');
      const widgetToggle = document.getElementById('ai-chat-toggle');
      const widgetStyles = document.getElementById('ai-chat-widget-styles');
      
      if (widgetContainer) widgetContainer.remove();
      if (widgetToggle) widgetToggle.remove();
      if (widgetStyles) widgetStyles.remove();
    };
  }, []);

  return (
    <div className="landing-page">
      <GridBackground />
      {/* Warning Banner */}
      <div className="warning-banner">
        <div className="warning-content">
          <span className="warning-icon">⚠️</span>
          <span>For production use, please contact the developer to ensure server availability and support.</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Smart Web App
              <span className="gradient-text"> AI Assistant</span>
            </h1>
            <p className="hero-subtitle">
              Open-source AI chat widget powered by Google Gemini that can be embedded into any website. 
              Create intelligent conversational experiences for your users.
            </p>
            <div className="hero-buttons">
              <Link to="/login" className="btn btn-primary">
                Get Started
              </Link>
              <a 
                href="https://github.com/nihalashetty/embedding-vectordb-llm-starter" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-secondary"
              >
                View on GitHub
              </a>
            </div>
          </div>
          <div className="hero-visual">
            <div className="chat-preview">
              <div className="chat-header">
                <span>Smart web app</span>
                <span>×</span>
              </div>
              <div className="chat-messages">
                <div className="message ai-message">
                  Welcome to Smart Web App! This is a preview of our AI assistant.
                </div>
                <div className="message user-message">
                  Tell me about this project
                </div>
                <div className="message ai-message">
                  This is an AI-powered chat widget that can be embedded...
                </div>
              </div>
              <div className="chat-input">
                <input placeholder="Ask me anything...." />
                <button>→</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="workflow-section">
        <div className="container">
          <h2 className="section-title">AI Documentation Workflow</h2>
          <p className="workflow-intro">From account setup to live AI-powered documentation widget - follow the complete automation process</p>
          
          <div className="workflow-diagram">
            <div className="workflow-flow">
              {/* Step 1 - Create Account */}
              <div className="workflow-card card-1 card-left">
                <div className="card-content">
                  <h3 className="card-title">Create Account</h3>
                  <span className="card-subtitle">User Action</span>
                  <p className="card-description">
                    Register your account. Get access to the dashboard 
                    where you can create and manage all your projects.
                  </p>
                </div>
              </div>

              {/* Connection Line 1 */}
              <div className="flow-connection connection-1">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 2 - Create Project */}
              <div className="workflow-card card-2 card-right">
                <div className="card-content">
                  <h3 className="card-title">Create Project</h3>
                  <span className="card-subtitle">User Action</span>
                  <p className="card-description">
                    Set up a dedicated workspace for your codebase documentation. Each project 
                    gets its own isolated environment with customizable settings and access 
                    controls.
                  </p>
                </div>
              </div>

              {/* Connection Line 2 */}
              <div className="flow-connection connection-2">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 3 - Configure Settings */}
              <div className="workflow-card card-3 card-left">
                <div className="card-content">
                  <h3 className="card-title">Configure Settings</h3>
                  <span className="card-subtitle">User Action</span>
                  <p className="card-description">
                    Add gemini API credentials. Customize the 
                    system prompts to match your documentation style and technical 
                    requirements and add your domain where you want to see the widget.
                  </p>
                </div>
              </div>

              {/* Connection Line 3 */}
              <div className="flow-connection connection-3">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 4 - Upload Files */}
              <div className="workflow-card card-4 card-right">
                <div className="card-content">
                  <h3 className="card-title">Upload Files</h3>
                  <span className="card-subtitle">Auto Process</span>
                  <p className="card-description">
                    Submit documentation files. The system validates, extracts 
                    content, and stores files securely in cloud storage for persistent 
                    access.
                  </p>
                </div>
              </div>

              {/* Connection Line 4 */}
              <div className="flow-connection connection-4">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 5 - Content Chunking */}
              <div className="workflow-card card-5 card-left">
                <div className="card-content">
                  <h3 className="card-title">Content Chunking</h3>
                  <span className="card-subtitle">Auto Process</span>
                  <p className="card-description">
                    Automatically split large documents into meaningful segments while 
                    preserving context. Smart chunking ensures optimal retrieval and AI 
                    processing efficiency.
                  </p>
                </div>
              </div>

              {/* Connection Line 5 */}
              <div className="flow-connection connection-5">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 6 - Vector Embedding */}
              <div className="workflow-card card-6 card-right">
                <div className="card-content">
                  <h3 className="card-title">Vector Embedding</h3>
                  <span className="card-subtitle">Auto Process</span>
                  <p className="card-description">
                    Generate high-dimensional semantic representations and store in ChromaDB 
                    database. Smart indexing enables fast context matching and retrieval.
                  </p>
                </div>
              </div>

              {/* Connection Line 6 */}
              <div className="flow-connection connection-6">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 7 - Vector Storage */}
              <div className="workflow-card card-7 card-left">
                <div className="card-content">
                  <h3 className="card-title">Vector Storage</h3>
                  <span className="card-subtitle">Auto Process</span>
                  <p className="card-description">
                    Store vectors in ChromaDB database optimized for similarity search. 
                    Efficient storage enables lightning-fast context retrieval for user 
                    queries.
                  </p>
                </div>
              </div>

              {/* Connection Line 7 */}
              <div className="flow-connection connection-7">
                <div className="connection-line"></div>
                <div className="connection-dot"></div>
              </div>

              {/* Step 8 - Widget Ready */}
              <div className="workflow-card card-8 card-right">
                <div className="card-content">
                  <h3 className="card-title">Widget Ready</h3>
                  <span className="card-subtitle">Live Deploy</span>
                  <p className="card-description">
                    AI-powered documentation widget is now live! Users can ask questions and 
                    receive contextual answers. Integration script is ready for deployment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Widget Integration Section */}
      <section className="customization-section">
        <div className="container">
          <h2 className="section-title">Widget Integration</h2>
          <div className="customization-grid">
            <div className="customization-info">
              <h3>Use Our Widget</h3>
              <p style={{color: '#a0a0a0', marginBottom: '20px'}}>
                Quick integration with our pre-built chat widget. Customizable with CSS.
              </p>
              
              <div className="integration-section">
                <h4>Integration Script</h4>
                <div className="code-block">
                  <pre><code>{`<script 
  src="https://smartweb-ai-backend-343646917543.europe-west1.run.app/widget.js" 
  data-api-key='YOUR_API_KEY' 
  data-project-id="YOUR_PROJECT_ID">
</script>`}</code></pre>
                </div>
              </div>
            </div>
            
            <div className="customization-example">
              <h3>Custom Implementation</h3>
              <p style={{color: '#a0a0a0', marginBottom: '20px'}}>
                Create your own widget design and use our API directly.
              </p>
              
              <div className="integration-section">
                <h4>API Endpoint</h4>
                <div className="code-block">
                  <pre><code>{`POST https://smartweb-ai-backend-343646917543.europe-west1.run.app/chat
Content-Type: application/json
X-API-Key: YOUR_API_KEY`}</code></pre>
                </div>
              </div>

              <div className="integration-section">
                <h4>Request Body</h4>
                <div className="code-block">
                  <pre><code>{`{
  "message": "User's question here",
  "projectId": "YOUR_PROJECT_ID"
}`}</code></pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">100%</div>
              <div className="stat-label">Open Source</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">&lt; 5min</div>
              <div className="stat-label">Setup Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">∞</div>
              <div className="stat-label">Customizable</div>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Section */}
      <section className="developer-section">
        <div className="container">
          <div className="developer-card">
            <div className="developer-info">
              <div className="developer-avatar">
                <img src="/profilepic.jpg" alt="Nihal A Shetty" />
              </div>
              <div className="developer-details">
                <h3>Nihal A Shetty</h3>
                <p>Web Developer & AI Enthusiast</p>
                <p>
                  Creative Frontend Developer from Bangalore. Working with companies and individuals around the globe I met and learnt from amazing and ambitious people. With every line of code, I strive to make the web a beautiful place.
                </p>
                <div className="developer-links">
                  <a href="mailto:nihalshetty48@gmail.com" className="dev-link">
                    Contact
                  </a>
                  <a href="https://github.com/nihalashetty/embedding-vectordb-llm-starter" target="_blank" rel="noopener noreferrer" className="dev-link">
                    GitHub
                  </a>
                  <a href="https://www.linkedin.com/in/nihal-a-shetty/" target="_blank" rel="noopener noreferrer" className="dev-link">
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-text">
              <p>&copy; 2025 Smart Web App. Open source project by Nihal Shetty.</p>
            </div>
            <div className="footer-links">
              <Link to="/login">Login</Link>
              <a href="https://github.com/nihalashetty/embedding-vectordb-llm-starter" target="_blank" rel="noopener noreferrer">
                GitHub
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
